import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Text,
  Stack,
  Flex,
  HStack,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { HelivanText } from '../helivan/HelivanText';
import { HelivanColor } from '../helivan/HelivanColor';
import { RiSparklingLine, RiUserLine, RiBarChartBoxLine } from 'react-icons/ri';

interface SurveyGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectOption: (option: 'generated' | 'partial' | 'human') => void;
}

export const SurveyGenerationModal = ({
  isOpen,
  onClose,
  onSelectOption,
}: SurveyGenerationModalProps) => {
  const toast = useToast();

  const handleUseGeneratedPersonas = () => {
    onSelectOption('generated');
    toast({
      title: 'Survey submitted',
      description: 'Your survey is being generated with AI perspectives',
      status: 'success',
      duration: 5000,
      isClosable: true,
    });
    onClose();
  };

  const handlePartialHumanIntervention = () => {
    onSelectOption('partial');
    toast({
      title: 'Survey submitted',
      description:
        'Your survey has been submitted for partial human intervention',
      status: 'success',
      duration: 5000,
      isClosable: true,
    });
    onClose();
  };

  const handleFullHumanIntervention = () => {
    onSelectOption('human');
    toast({
      title: 'Survey submitted',
      description: 'Your survey has been submitted for human intervention',
      status: 'success',
      duration: 5000,
      isClosable: true,
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent bg={HelivanColor.background.primary}>
        <ModalHeader>
          <HelivanText variant="sectionHeader">Survey Results</HelivanText>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={6} align="stretch" pb={6}>
            <Box p={4} borderWidth="1px" borderRadius="md" bg="blue.50">
              <Flex align="center" mb={2}>
                <RiSparklingLine size={20} color="blue" />
                <HelivanText variant="largeSubheader" ml={2}>
                  All answers were able to be generated automatically
                </HelivanText>
              </Flex>
              <Text>
                All Personas used to generate synthetic survey responses are
                based on real surveys from real people
              </Text>
            </Box>

            <Box>
              <HelivanText variant="largeSubheader" mb={2}>
                Select from the following:
              </HelivanText>
              <Stack spacing={4}>
                <Box
                  p={4}
                  borderWidth="1px"
                  borderRadius="md"
                  cursor="pointer"
                  onClick={handleUseGeneratedPersonas}
                  _hover={{ bg: 'gray.50' }}
                >
                  <Flex justify="space-between" align="center" gap={1}>
                    <HStack>
                      <RiUserLine size={20} />
                      <VStack align="start" spacing={0}>
                        <HelivanText variant="largeBody" colorOverride="black">
                          Use fully generated perspectives
                        </HelivanText>
                        <Text fontSize="sm" color="gray.500">
                          ⚡⚡⚡ Fastest 💲 Cheapest 🎯 Least Accurate
                        </Text>
                      </VStack>
                    </HStack>
                    <HelivanText variant="largeBody" colorOverride="blue.500">
                      $0.10/response
                    </HelivanText>
                  </Flex>
                </Box>

                <Box
                  p={4}
                  borderWidth="1px"
                  borderRadius="md"
                  cursor="pointer"
                  onClick={handlePartialHumanIntervention}
                  _hover={{ bg: 'gray.50' }}
                >
                  <Flex justify="space-between" gap={1} align="center">
                    <HStack>
                      <Box width="20">
                        <RiBarChartBoxLine size={20} />
                      </Box>
                      <VStack align="start" spacing={0}>
                        <HelivanText variant="largeBody" colorOverride="black">
                          Get partial human intervention
                        </HelivanText>
                        <Text fontSize="sm" color="gray.500">
                          Work 1:1 with our team to create a survey that can be
                          partially completed by humans and partially completed
                          by their persona.
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          ⚡⚡ Not as fast 💲💲 Not as cheap 🎯🎯 More Accurate
                        </Text>
                      </VStack>
                    </HStack>
                    <HelivanText variant="largeBody" colorOverride="blue.500">
                      $0.50/response
                    </HelivanText>
                  </Flex>
                </Box>

                <Box
                  p={4}
                  borderWidth="1px"
                  borderRadius="md"
                  cursor="pointer"
                  onClick={handleFullHumanIntervention}
                  _hover={{ bg: 'gray.50' }}
                >
                  <Flex justify="space-between" align="center" gap={1}>
                    <HStack>
                      <RiBarChartBoxLine size={20} />
                      <VStack align="start" spacing={0}>
                        <HelivanText variant="largeBody" colorOverride="black">
                          Get human intervention
                        </HelivanText>
                        <Text fontSize="sm" color="gray.500">
                          ⚡ Slowest 💲💲💲 Most Expensive 🎯🎯 Most Accurate
                        </Text>
                      </VStack>
                    </HStack>
                    <HelivanText variant="largeBody" colorOverride="blue.500">
                      $1/response
                    </HelivanText>
                  </Flex>
                </Box>
              </Stack>
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
